import { lazy, useEffect, useRef } from "react";
import DefaultLayout from "~/layouts/DefaultLayout";
import LoginLayout from "~/layouts/LoginLayout";
import { Routes, Route, Navigate, useLocation } from "react-router";
import ProtectedRoute from "~/components/router/ProtectedRoute";
import PermissionGuard from "~/components/router/PermissionGuard";
import { Permission } from "~/helpers/permissions.helper";
import { useTelemetry } from "~/hooks/useTelemetry";
import { resolveRoutePattern } from "~/helpers/telemetry.helper";
import { setTelemetryView } from "~/telemetry";
import type { RoutePattern } from "~/types/telemetry";

const LoginPage = lazy(() => import("~/pages/login"));
const ReportListPage = lazy(() => import("~/pages/report/list"));
const ReportDetailsPage = lazy(() => import("~/pages/report/details"));
const AccountPage = lazy(() => import("~/pages/account"));
const NoPermissionPage = lazy(() => import("~/pages/no-permission"));

/**
 * Protótipos de layout (dados mockados) — ver src/prototypes/home-search.
 *
 * A flag é lida de `import.meta.env`, que o Vite substitui por literal no build:
 * com ela diferente de "true" a condição vira `false` e o `import()` dinâmico é
 * eliminado, então o protótipo não entra em nenhum chunk do bundle.
 */
const PROTOTYPES_ENABLED = import.meta.env.VITE_PROTOTYPES_ENABLED === "true";

const HomeSearchPrototypePage = PROTOTYPES_ENABLED
  ? lazy(() => import("~/prototypes/home-search"))
  : null;

/**
 * Emits the catalog `navigation` event and attributes the Faro view whenever
 * the resolved route **pattern** changes — never on every pathname change (a
 * `:id` changing under the same pattern, e.g. switching report details,
 * is not a pattern change) and never with a concrete URL/id (Req 12.7, 13.5).
 */
function useNavigationTelemetry(): void {
  const location = useLocation();
  const { emit } = useTelemetry();
  const lastPatternRef = useRef<RoutePattern | null>(null);

  useEffect(() => {
    const routePattern = resolveRoutePattern(location.pathname);
    if (lastPatternRef.current === routePattern) {
      return;
    }
    lastPatternRef.current = routePattern;
    setTelemetryView(routePattern);
    emit({ name: "navigation", routePattern });
  }, [location.pathname, emit]);
}

const Router = () => {
  useNavigationTelemetry();

  return (
    <Routes>
      {/* Rotas públicas */}
      <Route element={<LoginLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Protótipo de layout: público e sem sessão (todo o conteúdo é mockado),
          fora do ProtectedRoute e do DefaultLayout — renderiza o próprio shell.
          Só existe quando VITE_PROTOTYPES_ENABLED=true. */}
      {HomeSearchPrototypePage && (
        <Route path="/prototipo/*" element={<HomeSearchPrototypePage />} />
      )}

      {/* Rotas protegidas por autenticação */}
      <Route element={<ProtectedRoute />}>
        {/* Alias do redirect pós-login do Keycloak (FRONTEND_REDIRECT_URL termina em
            /dashboard). Sem esta rota, o callback OAuth e o link de convite caem em
            tela em branco porque nada casa no Routes. */}
        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route element={<DefaultLayout />}>
          {/* Página inicial - requer permissão para visualizar lista de relatórios */}
          <Route
            path="/"
            element={
              <PermissionGuard requiredPermission={Permission.VIEW_REPORT_LIST}>
                <ReportListPage />
              </PermissionGuard>
            }
          />

          {/* Pasta - requer permissão para visualizar lista de relatórios */}
          <Route
            path="/pasta/:folderId"
            element={
              <PermissionGuard requiredPermission={Permission.VIEW_REPORT_LIST}>
                <ReportListPage />
              </PermissionGuard>
            }
          />

          {/* Detalhes do relatório - requer permissão para visualizar detalhes */}
          <Route
            path="report/:type/:id"
            element={
              <PermissionGuard requiredPermission={Permission.VIEW_REPORT_DETAILS}>
                <ReportDetailsPage />
              </PermissionGuard>
            }
          />

          {/* Configurações da conta - sempre acessível para usuários autenticados */}
          <Route
            path="/conta/configuracoes"
            element={<AccountPage />}
          />

          {/* Suporte da conta - sempre acessível para usuários autenticados */}
          <Route
            path="/conta/suporte"
            element={<AccountPage />}
          />

          {/* Página quando usuário não tem acesso a rota - sem permissão - sempre acessível para usuários autenticados */}
          <Route path="/sem-permissao" element={<NoPermissionPage />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default Router;
